<template>
  <b-row class="m-3">
    <b-col md="8" offset-md="2" lg="6" offset-lg="3">
      <!-- Success state -->
      <b-card v-if="emailSent" header="Recovery Email Sent" header-bg-variant="success" header-text-variant="white" bg-variant="light">
        <p class="mb-3">
          An account recovery link has been sent to the email address on file for your account. Please check your inbox and follow the instructions.
        </p>
        <p class="mb-3" style="font-size:0.9rem;">
          If you do not receive the email within the next <strong>15 minutes</strong>, check your Junk/Spam folder or
          <b-link @click="resetForm">try again</b-link>.
        </p>
        <div class="text-right">
          <b-button size="sm" variant="primary" :to="{ name: 'login' }">Back to Login</b-button>
        </div>
      </b-card>

      <!-- Request form -->
      <b-card v-else header="Account Recovery" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <p class="text-muted mb-3" style="font-size:0.9rem;">
          Use this form if you have forgotten your username <em>and</em> password. If you know your username,
          <b-link :to="{ name: 'request-password' }">reset your password instead</b-link>.
        </p>

        <b-form @submit.prevent="onSubmit">
          <b-form-group label="UBC ID:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.ubcid" v-mask="'U-####-####'" :state="validated('ubcid')" placeholder="e.g. U-1234-5678" trim />
            <b-form-invalid-feedback>A valid UBC ID is required</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Last Name:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.lastname" :state="validated('lastname')" placeholder="Last Name" trim />
            <b-form-invalid-feedback>Last Name is required</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Date of Birth:" label-cols-sm="4" label-align-sm="right">
            <b-input type="date" v-model="form.birthdate" :state="validated('birthdate')" />
            <b-form-invalid-feedback>Invalid date</b-form-invalid-feedback>
            <b-form-text>Required if you do not have an SSN/SIN on file.</b-form-text>
          </b-form-group>

          <b-form-group label="Last 4 of SSN/SIN:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="form.ssn" v-mask="'####'" :state="validated('ssn')" placeholder="Last 4 digits" trim />
            <b-form-text>Required if you have an SSN/SIN on file.</b-form-text>
          </b-form-group>

          <div class="d-flex justify-content-between align-items-center mt-3">
            <b-button size="sm" variant="secondary" :disabled="loading" :to="{ name: 'login' }">Cancel</b-button>
            <b-button size="sm" type="submit" variant="success" :disabled="loading || submitDisabled">
              <b-spinner v-if="loading" small class="mr-1" />
              Send Recovery Email
            </b-button>
          </div>
        </b-form>
      </b-card>

      <recaptcha-v2-modal
        v-model="showV2Challenge"
        :site-key="recaptchaV2SiteKey"
        @resolved="onV2Resolved"
        @cancelled="onV2Cancelled"
        @error="onV2Error"
      />
    </b-col>
  </b-row>
</template>
<script>
import { Vue, Component, Mixins } from 'vue-property-decorator';
import memberDao from '@/dao/member_dao';
import { isValid as isValidUbcId } from '@/util/ubc_id';
import { isValidDobString } from '@/util/date';
import RecaptchaV2Modal from '@/components/RecaptchaV2Modal.vue';
import recaptchaMixin from '@/mixins/recaptcha_fallback';
import { renderErrorBody } from '@/util/error';

@Component({
  metaInfo: {
    title: 'Member Portal - Account Recovery'
  },
  components: { RecaptchaV2Modal }
})
export default class AccountRecovery extends Mixins(recaptchaMixin) {
  form = {
    ubcid: '',
    lastname: '',
    birthdate: '',
    ssn: ''
  };
  loading = false;
  submitted = false;
  emailSent = false;

  get submitDisabled() {
    // UBC ID and last name are always required; at least one of birthdate or SSN must be present
    if (!this.validated('ubcid') || !this.validated('lastname')) return true;
    const hasDate = (this.form.birthdate || '').trim().length > 0;
    const hasSsn = (this.form.ssn || '').replace(/\D/g, '').length === 4;
    return !hasDate && !hasSsn;
  }

  validated(field) {
    if (!this.submitted && !this.form[field]) return undefined;
    const value = (this.form[field] || '').trim();
    switch (field) {
      case 'ubcid':
        return isValidUbcId(value);
      case 'lastname':
        return value.length > 0;
      case 'birthdate': {
        if (!value.length) return undefined;
        return isValidDobString(value);
      }
      case 'ssn': {
        const digits = value.replace(/\D/g, '');
        return digits.length === 0 ? undefined : digits.length === 4;
      }
      default:
        return true;
    }
  }

  mounted() {
    this.loadRecaptcha();
  }

  resetForm() {
    this.emailSent = false;
    this.submitted = false;
    this.form = { ubcid: '', lastname: '', birthdate: '', ssn: '' };
  }

  async onSubmit() {
    try {
      this.submitted = true;
      if (this.submitDisabled) {
        return this.$bvToast.toast('Please correct the highlighted errors.', {
          title: 'Error',
          solid: true,
          variant: 'danger'
        });
      }
      this.loading = true;

      const payload = { ubcid: this.form.ubcid.trim(), lastname: this.form.lastname.trim() };
      if (this.form.birthdate) payload.birthdate = this.form.birthdate.trim();
      if (this.form.ssn) payload.ssn = this.form.ssn.replace(/\D/g, '');
      await this.submitWithRecaptcha('account_recovery', tokens =>
        memberDao.requestAccountRecovery({ ...payload, ...tokens })
      );
      this.emailSent = true;
    } catch (error) {
      const body = renderErrorBody(this.$createElement, (error && error.message) || 'An unknown error occurred, please try again.');
      this.$bvModal.msgBoxOk(body, {
        title: 'Unable to Send Recovery Email',
        okTitle: 'OK',
        okVariant: 'danger',
        centered: true,
        headerBgVariant: 'danger',
        headerTextVariant: 'white'
      });
    } finally {
      this.loading = false;
    }
  }
}
</script>
<style scoped></style>
